/**
 * What `GET /hq/user/permissions` answers, and therefore what `usePermissions()` hands back.
 *
 * `allowed` is every ACL resource the signed-in admin can reach, already widened server side to
 * include everything inherited from a granted ancestor, so nothing here needs to know the shape of
 * the tree.
 *
 * `roles` is the old comma-separated `ROLE_*` string. **Nothing in HQ reads it any more** as of
 * Stage 5, and HQ-API does not authorise an admin on it either. It is still sent because the
 * column is kept as a record of what somebody's access was before the ACL, and because an open tab
 * across a deploy would otherwise be handed a payload missing a field it expects.
 */
export interface AclPermissions {
    roles: string;
    role: string | null;
    allowed: string[];
}

/**
 * Anything `usePermissions()` can hand back.
 *
 * Three shapes reach the helpers below and all three have to be tolerated. The object above is the
 * live one. A bare string is what the endpoint answered before 2026-09-10, and an open tab across
 * a deploy still holds one. An empty array is what `authProvider.getPermissions()` returns on every
 * failure path, including "not signed in yet", which is why none of this may throw.
 */
export type Permissions = AclPermissions | string | unknown;

/**
 * Every permission check in HQ goes through here.
 *
 * It held five `ROLE_*` helpers until 2026-09-10 and holds none now. They went in stages as the
 * screens using them moved to ACL resources; the last two, `isSuperAdmin` and `isAccounts`, went
 * in Stage 5 along with the transitional menu gates they were the second half of.
 *
 * **Nothing should be added back that answers a question about a role name.** A helper like that
 * is a ready-made way to gate a screen on something other than the ACL, and the point of the
 * migration is that there is one answer to "may this admin do this", and the server gives it.
 */
export const permissionManager = {
    /**
     * Whether the admin may use an ACL resource, such as `HQ_Sales::conversation_reassign`.
     *
     * It reads the server's answer rather than deriving anything: the API expanded the tree
     * already, and re-deriving it here would put a rule the server enforces somewhere the server
     * cannot see.
     *
     * Answers false when the payload carries no grants at all, which is the safe direction: a
     * control gated on this is hidden rather than shown while the answer is unknown.
     */
    isAllowed: (permissions: Permissions, resource: string): boolean => {
        const allowed: unknown = (permissions as Partial<AclPermissions> | null)?.allowed;

        return Array.isArray(allowed) && allowed.includes(resource);
    },

    /** The name of the ACL role the admin holds, or null while none is assigned. */
    roleName: (permissions: Permissions): string | null => {
        const role: unknown = (permissions as Partial<AclPermissions> | null)?.role;

        return typeof role === 'string' && role.length > 0 ? role : null;
    }
}
